/* eslint-disable react-hooks/rules-of-hooks, @typescript-eslint/no-explicit-any */
import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  fetchReviews, 
  fetchReviewStats, 
  fetchGMBLocations, 
  fetchServices,
  fetchReviewById,
  createReviewResponse,
  updateReviewResponse,
  autoTagReviews
} from '@/utils/reviewsApi'
import type { ReviewStats, ReviewTableData, ReviewsQuery } from '@/types/reviews'

const isTest = import.meta.env.MODE === 'test'

export const reviewsKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewsKeys.all, 'list'] as const,
  list: (query: ReviewsQuery) => [...reviewsKeys.lists(), query] as const,
  details: () => [...reviewsKeys.all, 'detail'] as const,
  detail: (id: string) => [...reviewsKeys.details(), id] as const,
  stats: (source?: string) => [...reviewsKeys.all, 'stats', source] as const,
  locations: () => ['gmb-locations'] as const,
  services: () => ['services'] as const,
}

function useTestQuery<T>(fn: () => Promise<T> | T, deps: React.DependencyList = []) {
  const [state, setState] = React.useState<any>({
    data: undefined,
    error: null,
    isLoading: true,
    isError: false,
    isSuccess: false
  })

  React.useEffect(() => {
    let active = true
    Promise.resolve()
      .then(() => fn())
      .then((data) => {
        if (!active) return
        setState({ data, error: null, isLoading: false, isError: false, isSuccess: true })
      })
      .catch((error) => {
        if (!active) return
        setState({ data: undefined, error, isLoading: false, isError: true, isSuccess: false })
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return state
}

function useReviewsTestImpl(query: ReviewsQuery = {}) {
  const queryClient = useQueryClient()
  const [state, setState] = React.useState<{
    data: ReviewTableData | undefined
    error: Error | null
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
  }>({
    data: undefined,
    error: null,
    isLoading: true,
    isError: false,
    isSuccess: false,
  })

  React.useEffect(() => {
    let active = true

    const queryKey = reviewsKeys.list(query)
    const cached = queryClient.getQueryData?.(queryKey) as ReviewTableData | undefined
    if (cached) {
      setState({ data: cached, error: null, isLoading: false, isError: false, isSuccess: true })
      return () => {
        active = false
      }
    }

    setState(s => ({ ...s, isLoading: true, isError: false, isSuccess: false }))
    fetchReviews(query)
      .then(data => {
        if (!active) return
        queryClient.setQueryData(queryKey, data)
        setState({ data, error: null, isLoading: false, isError: false, isSuccess: true })
      })
      .catch(error => {
        if (!active) return
        setState({ data: undefined, error: error instanceof Error ? error : new Error(String(error)), isLoading: false, isError: true, isSuccess: false })
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, JSON.stringify(query)])

  return state
}

function useReviewsProdImpl(query: ReviewsQuery = {}) {
  return useQuery<ReviewTableData, Error>({
    queryKey: reviewsKeys.list(query),
    queryFn: () => fetchReviews(query),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  })
}

export function useReviews(query: ReviewsQuery = {}) {
  return isTest ? useReviewsTestImpl(query) : useReviewsProdImpl(query)
}

function useReviewStatsTestImpl(source?: string) {
  return useTestQuery(() => fetchReviewStats(source), [source])
}

function useReviewStatsProdImpl(source?: string) {
  return useQuery<ReviewStats, Error>({
    queryKey: reviewsKeys.stats(source),
    queryFn: () => fetchReviewStats(source),
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
  })
}

/**
 * Fetch review statistics with optional source filter
 * @param source - Optional source filter ('gmb', 'manual', etc.)
 */
export function useReviewStats(source?: string) {
  return isTest ? useReviewStatsTestImpl(source) : useReviewStatsProdImpl(source)
}

function useGMBLocationsTestImpl() {
  return useTestQuery(fetchGMBLocations, [])
}

function useGMBLocationsProdImpl() {
  return useQuery({
    queryKey: reviewsKeys.locations(),
    queryFn: fetchGMBLocations,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  })
}

export function useGMBLocations() {
  return isTest ? useGMBLocationsTestImpl() : useGMBLocationsProdImpl()
}

function useServicesTestImpl() {
  return useTestQuery(fetchServices, [])
}

function useServicesProdImpl() {
  return useQuery({
    queryKey: reviewsKeys.services(),
    queryFn: fetchServices,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  })
}

export function useServices() {
  return isTest ? useServicesTestImpl() : useServicesProdImpl()
}

function useReviewTestImpl(id: string) {
  if (!id) {
    return { data: undefined, isLoading: false, isError: false, isSuccess: false, error: null }
  }
  return useTestQuery(() => fetchReviewById(id), [id])
}

function useReviewProdImpl(id: string) {
  return useQuery({
    queryKey: reviewsKeys.detail(id),
    queryFn: () => fetchReviewById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  })
}

export function useReview(id: string) {
  return isTest ? useReviewTestImpl(id) : useReviewProdImpl(id)
}

export function useCreateReviewResponse() {
  const queryClient = useQueryClient()

  if (isTest) {
    const [state, setState] = React.useState({ isSuccess: false, isError: false, isPending: false, error: null as unknown })
    const mutate = ({ reviewId, responseText, publishToGoogle = false }: { reviewId: string; responseText: string; publishToGoogle?: boolean }) => {
      setState((s) => ({ ...s, isPending: true }))
      Promise.resolve()
        .then(() => createReviewResponse({ review_id: reviewId, response_text: responseText, sync_to_gmb: publishToGoogle }))
        .then(() => {
          setState({ isSuccess: true, isError: false, isPending: false, error: null })
          queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
          queryClient.invalidateQueries({ queryKey: reviewsKeys.stats() })
          queryClient.invalidateQueries({ queryKey: reviewsKeys.detail(reviewId) })
        })
        .catch((error) => setState({ isSuccess: false, isError: true, isPending: false, error }))
    }
    return { mutate, ...state }
  }
  
  return useMutation({
    mutationFn: ({
      reviewId,
      responseText,
      publishToGoogle = false
    }: {
      reviewId: string
      responseText: string
      publishToGoogle?: boolean
    }) => createReviewResponse({ review_id: reviewId, response_text: responseText, sync_to_gmb: publishToGoogle }),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: reviewsKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: reviewsKeys.detail(variables.reviewId) })
    },
  })
}

export function useUpdateReviewResponse() {
  const queryClient = useQueryClient()

  if (isTest) {
    const [state, setState] = React.useState({ isSuccess: false, isError: false, isPending: false, error: null as unknown })
    const mutate = ({ responseId, responseText, publishToGoogle = false }: { responseId: string; responseText: string; publishToGoogle?: boolean }) => {
      setState((s) => ({ ...s, isPending: true }))
      Promise.resolve()
        .then(() => updateReviewResponse(responseId, { response_text: responseText, sync_to_gmb: publishToGoogle }))
        .then(() => {
          setState({ isSuccess: true, isError: false, isPending: false, error: null })
          queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
          queryClient.invalidateQueries({ queryKey: reviewsKeys.stats() })
        })
        .catch((error) => setState({ isSuccess: false, isError: true, isPending: false, error }))
    }
    return { mutate, ...state }
  }
  
  return useMutation({
    mutationFn: ({
      responseId,
      responseText,
      publishToGoogle = false
    }: {
      responseId: string
      responseText: string
      publishToGoogle?: boolean
    }) => updateReviewResponse(responseId, { response_text: responseText, sync_to_gmb: publishToGoogle }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: reviewsKeys.stats() })
    },
  })
}

export function useAutoTagReviews() {
  const queryClient = useQueryClient()

  if (isTest) {
    const [state, setState] = React.useState({ isSuccess: false, isError: false, isPending: false, error: null as unknown })
    const mutate = (reviewIds?: string[]) => {
      setState((s) => ({ ...s, isPending: true }))
      Promise.resolve()
        .then(() => autoTagReviews(reviewIds))
        .then(() => {
          setState({ isSuccess: true, isError: false, isPending: false, error: null })
          queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
        })
        .catch((error) => setState({ isSuccess: false, isError: true, isPending: false, error }))
    }
    return { mutate, ...state }
  }
  
  return useMutation({
    mutationFn: (reviewIds?: string[]) => autoTagReviews(reviewIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() })
    },
  })
}
